import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Alert, Badge, Button, Progress, RadioGroup, Skeleton, TextArea } from '../ui';
import { ConfidenceInput } from './ConfidenceInput';
import { MasteryMark } from './MasteryMark';
import {
  MISSION_STEPS,
  SUBMITTABLE_STEPS,
  clearDraft,
  findDraftForMission,
  missionErrorKeys,
  readDraft,
  useMissions,
  writeDraft,
} from '../../context/MissionContext';

/**
 * MissionRunner (spec §7.3, §9.3) — one mission, five steps, one at a time.
 *
 *   1 Abrufen     free recall
 *   2 Anwenden    a realistic work case (a *variant*, so transfer is measurable)
 *   3 Belegen     pick the source/rule among distractors
 *   4 Konfidenz   captured BEFORE the resolution exists on the client
 *   5 Feedback    explanation, calibration read-out, next sensible action
 *
 * Three invariants:
 *
 *  - **The resolution cannot be peeked at.** Correct answers live only in the
 *    response of `POST …/complete`, which the engine refuses before a
 *    confidence was recorded. There is nothing in this component's state to
 *    reveal early, so no UI mistake can leak it.
 *  - **Nothing typed is ever lost** (spec §11.1). Every keystroke, every
 *    selection and the current step are mirrored into
 *    `etch.mission.<attemptId>`; a reload, a crash or a failed request resumes
 *    exactly where the learner was, and a failed submit keeps the input on
 *    screen instead of clearing the form.
 *  - **A wrong answer costs nothing** (spec §2.3). Failure opens a retry and a
 *    learning path; there is no score to lose, no streak to break and no
 *    punishing copy anywhere in the feedback step.
 */

const TOTAL_STEPS = MISSION_STEPS.length;

/** Confidence 1..5 mapped onto 0..1 so it is comparable with correctness. */
function normaliseConfidence(value, scaleMax = 5) {
  if (!Number.isFinite(Number(value)) || scaleMax <= 1) return null;
  return Math.min(1, Math.max(0, (Number(value) - 1) / (scaleMax - 1)));
}

/** Which of the three calibration sentences the feedback step shows. */
function calibrationVerdict(confidence, correctness, scaleMax = 5) {
  const normalised = normaliseConfidence(confidence, scaleMax);
  if (normalised === null || correctness === null || correctness === undefined) return null;
  const delta = normalised - correctness;
  if (delta > 0.25) return 'overconfident';
  if (delta < -0.25) return 'underconfident';
  return 'good';
}

function correctnessLabelKey(correctness) {
  if (correctness >= 0.7) return 'missions.correct';
  if (correctness >= 0.4) return 'missions.partly';
  return 'missions.incorrect';
}

function Stepper({ current }) {
  const { t } = useTranslation();
  const currentIndex = MISSION_STEPS.indexOf(current);

  return (
    <ol className="etch-mission-stepper">
      {MISSION_STEPS.map((step, index) => {
        const state = index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'todo';
        return (
          <li
            key={step}
            className="etch-mission-stepper__item"
            data-state={state}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span className="etch-mission-stepper__index" aria-hidden="true">
              {index + 1}
            </span>
            <span className="etch-mission-stepper__label">{t(`missions.step.${step}`)}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function MissionRunner({ missionId, onExit, onOpenSource }) {
  const { t } = useTranslation();
  const { getMission, startAttempt, submitStep, completeAttempt, offline, refresh } = useMissions();

  const [phase, setPhase] = useState('loading'); // loading | ready | fatal
  const [mission, setMission] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [attemptIndex, setAttemptIndex] = useState(1);
  const [step, setStep] = useState('recall');
  const [answers, setAnswers] = useState({ recall: '', apply: '', cite: null, confidence: null });
  const [submitted, setSubmitted] = useState([]);
  const [resolution, setResolution] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [stepError, setStepError] = useState(null);
  const [validation, setValidation] = useState(null);
  const [fatal, setFatal] = useState(null);
  const [restored, setRestored] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [bootNonce, setBootNonce] = useState(0);

  const startedAtRef = useRef(Date.now());
  const stepHeadingRef = useRef(null);
  // Keyed on the boot nonce so React 18's StrictMode double-invoke cannot
  // create two attempts, while an explicit retry still re-runs the boot.
  const bootedRef = useRef(-1);

  /* -- Boot: resume an in-flight attempt, or start a new one -------------- */

  const beginAttempt = useCallback(async () => {
    const started = await startAttempt(missionId);
    startedAtRef.current = Date.now();
    setMission(started.mission);
    setAttemptId(started.attempt.id);
    setAttemptIndex(started.attemptIndex ?? 1);
    setStep(started.step || 'recall');
    setAnswers({ recall: '', apply: '', cite: null, confidence: null });
    setSubmitted([]);
    setResolution(null);
    setRestored(false);
    writeDraft({
      attemptId: started.attempt.id,
      missionId,
      attemptIndex: started.attemptIndex ?? 1,
      step: started.step || 'recall',
      answers: { recall: '', apply: '', cite: null, confidence: null },
      submitted: [],
      startedAt: startedAtRef.current,
    });
    setPhase('ready');
  }, [missionId, startAttempt]);

  useEffect(() => {
    if (bootedRef.current === bootNonce) return;
    bootedRef.current = bootNonce;

    (async () => {
      setPhase('loading');
      setFatal(null);
      const draft = findDraftForMission(missionId);

      if (draft?.attemptId) {
        try {
          const { mission: loaded } = await getMission(missionId, draft.attemptId);
          // `steps.feedback` only exists once the attempt is resolved — a
          // finished run is not a draft, so start a fresh attempt instead.
          if (loaded?.steps?.feedback) {
            clearDraft(draft.attemptId);
          } else {
            const stored = readDraft(draft.attemptId) || draft;
            setMission(loaded);
            setAttemptId(draft.attemptId);
            setAttemptIndex(stored.attemptIndex ?? 1);
            setStep(SUBMITTABLE_STEPS.includes(stored.step) ? stored.step : 'recall');
            setAnswers({
              recall: stored.answers?.recall ?? '',
              apply: stored.answers?.apply ?? '',
              cite: stored.answers?.cite ?? null,
              confidence: stored.answers?.confidence ?? null,
            });
            setSubmitted(Array.isArray(stored.submitted) ? stored.submitted : []);
            startedAtRef.current = stored.startedAt ?? Date.now();
            setRestored(true);
            setPhase('ready');
            return;
          }
        } catch (err) {
          // A draft pointing at an attempt the server no longer knows is stale,
          // not a failure the learner should have to read about.
          if (err?.code === 'attempt_not_found' || err?.code === 'mission_not_found') {
            clearDraft(draft.attemptId);
          } else {
            setFatal(err);
            setPhase('fatal');
            return;
          }
        }
      }

      try {
        await beginAttempt();
      } catch (err) {
        setFatal(err);
        setPhase('fatal');
      }
    })();
  }, [bootNonce, missionId, getMission, beginAttempt]);

  /* -- Draft mirroring (spec §11.1) -------------------------------------- */

  useEffect(() => {
    if (!attemptId || phase !== 'ready' || step === 'feedback') return;
    writeDraft({
      attemptId, missionId, attemptIndex, step, answers, submitted,
      startedAt: startedAtRef.current,
    });
  }, [attemptId, missionId, attemptIndex, step, answers, submitted, phase]);

  /* -- Announce step changes and results (spec §12) ----------------------- */

  useEffect(() => {
    if (phase !== 'ready') return;
    if (step === 'feedback') return;
    const index = MISSION_STEPS.indexOf(step) + 1;
    setAnnouncement(
      `${t('a11y.liveMissionStep', { step: index, total: TOTAL_STEPS })} — ${t(`missions.step.${step}`)}`
    );
    // Focus follows the step heading so keyboard users are not dropped at the
    // top of the document on every transition.
    stepHeadingRef.current?.focus?.();
  }, [step, phase, t]);

  /* -- Submitting -------------------------------------------------------- */

  const goToStep = useCallback((next) => {
    setValidation(null);
    setStepError(null);
    setStep(next);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setValidation(null);
    setStepError(null);

    let payload;
    if (step === 'recall' || step === 'apply') {
      if (!answers[step]?.trim()) {
        setValidation(t('missions.answerRequired'));
        return;
      }
      payload = { answer: answers[step].trim() };
    } else if (step === 'cite') {
      if (!answers.cite) {
        setValidation(t('missions.optionRequired'));
        return;
      }
      payload = { optionId: answers.cite };
    } else if (step === 'confidence') {
      if (!answers.confidence) {
        setValidation(t('missions.confidenceRequired'));
        return;
      }
      payload = { confidence: answers.confidence };
    } else {
      return;
    }

    setSubmitting(true);
    try {
      const response = await submitStep(missionId, attemptId, step, payload);
      const nextSubmitted = submitted.includes(step) ? submitted : [...submitted, step];
      setSubmitted(nextSubmitted);

      if (step !== 'confidence') {
        setAnnouncement(t('a11y.liveSaved'));
        goToStep(response.nextStep && response.nextStep !== step ? response.nextStep : nextStepAfter(step));
        return;
      }

      // Confidence accepted → and only now may the resolution be fetched.
      const result = await completeAttempt(missionId, attemptId, Date.now() - startedAtRef.current);
      setResolution(result);
      clearDraft(attemptId);
      setStep('feedback');
      setAnnouncement(
        `${t('missions.resultAnnouncement', {
          result: t(correctnessLabelKey(result.correctness ?? 0)),
          level: t(`mastery.level.${result.mastery?.level ?? 'new'}`),
        })}`
      );
      refresh();
    } catch (err) {
      setStepError(err);
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting, step, answers, missionId, attemptId, submitted, submitStep, completeAttempt,
    goToStep, refresh, t,
  ]);

  const handleRetry = useCallback(async () => {
    setSubmitting(true);
    setStepError(null);
    try {
      await beginAttempt();
      setAnnouncement(t('missions.retryStarted'));
    } catch (err) {
      setFatal(err);
      setPhase('fatal');
    } finally {
      setSubmitting(false);
    }
  }, [beginAttempt, t]);

  const steps = mission?.steps ?? null;

  const citeOptions = useMemo(
    () =>
      (steps?.cite?.options ?? []).map((option) => ({
        value: option.id,
        label: option.label,
        hint: option.ref || undefined,
      })),
    [steps]
  );

  /* -- Non-happy paths (spec §11) ---------------------------------------- */

  if (phase === 'loading') {
    return (
      <div className="etch-mission-runner" data-state="loading">
        <p className="etch-visually-hidden" role="status">
          {t('states.loading')}
        </p>
        <Skeleton height="1.5rem" width="60%" />
        <Skeleton height="0.75rem" width="100%" />
        <Skeleton height="10rem" width="100%" />
      </div>
    );
  }

  if (phase === 'fatal') {
    return (
      <div className="etch-mission-runner" data-state="error">
        <Alert tone={fatal?.code === 'session_expired' ? 'warning' : 'danger'} title={t(missionErrorKeys(fatal))}>
          <p>{t('missions.runnerFailedBody')}</p>
          <div className="etch-row">
            <Button
              variant="secondary"
              size="compact"
              onClick={() => setBootNonce((nonce) => nonce + 1)}
            >
              {t('states.retry')}
            </Button>
            <Button variant="ghost" size="compact" onClick={onExit}>
              {t('missions.backToCenter')}
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  const currentIndex = MISSION_STEPS.indexOf(step) + 1;
  const previousStep = SUBMITTABLE_STEPS[SUBMITTABLE_STEPS.indexOf(step) - 1] ?? null;
  const canGoBack = step !== 'feedback' && step !== 'confidence' && previousStep !== null;

  return (
    <div className="etch-mission-runner" data-state={submitting ? 'busy' : 'ready'}>
      <p className="etch-visually-hidden" role="status" aria-live="polite">
        {announcement}
      </p>

      <header className="etch-mission-runner__header">
        <div className="etch-mission-runner__identity">
          <span className="etch-eyebrow">
            {t([`missions.origin.${mission?.origin ?? 'generated'}`, 'missions.title'])}
          </span>
          <h1 className="etch-mission-runner__title">{mission?.title}</h1>
          {mission?.scenario && <p className="etch-mission-runner__scenario">{mission.scenario}</p>}
        </div>
        <Button variant="ghost" size="compact" onClick={onExit}>
          {t('missions.backToCenter')}
        </Button>
      </header>

      {/* Art. 50 Abs. 1 AI Act: task and feedback may be AI-generated and may
          be wrong, and the feedback is a learning aid, not a graded
          assessment. role="note" rather than aria-describedby: unlike the
          composer's single input, there is no one field this notice is
          "for" — it applies to the whole run.

          This is deliberately unconditional for now, which is known to be
          too broad: POST /api/missions (the "Einprägen" authoring path,
          src/server/routes/missions.js -> MissionEngine.createMission() in
          src/learning/mission-engine.js) never calls an AI provider — it
          persists whatever `steps` the caller sends. Both chat's Einprägen
          action (EtchAction.jsx, real AI-answer text) and the Knowledge
          Explorer's selection Einprägen (KnowledgeExplorer.jsx, a static
          i18n template filled with the selected file/symbol/document/
          directory name — no LLM involved) POST through this same path with
          the same `origin: 'einpraegen'`, and neither the request payload
          nor the mission DTO carries a flag telling the two apart. Only
          POST /api/missions/generate (onboarding/challenge/generated-origin
          missions authored from a flashcard) actually calls
          provider.generateContent(). Narrowing this notice (and the
          data-ai-generated markers below) correctly needs a real signal
          threaded through EtchPreview.jsx's buildMissionPayload() and
          persisted by createMission() — tracked, not fixed here; see
          tests/unit/web/ai-act-marking-mission-origin.test.js. Until then
          the copy hedges ("may be") instead of asserting AI authorship as
          settled fact, which stays true either way. */}
      <p className="etch-mission-runner__disclosure" role="note">
        <Info className="etch-mission-runner__disclosure-icon" size={13} aria-hidden="true" />
        <span>{t('missions.aiDisclosure')}</span>
      </p>

      {offline && (
        <Alert tone="warning" title={t('states.offline')}>
          {t('missions.offlineBody')}
        </Alert>
      )}

      {restored && step !== 'feedback' && (
        <Alert tone="info" title={t('states.recovered')}>
          {t('missions.draftRestoredBody')}
        </Alert>
      )}

      <div className="etch-mission-runner__progress">
        <Stepper current={step} />
        <Progress
          value={currentIndex}
          max={TOTAL_STEPS}
          showValue={false}
          label={t('missions.progressLabel', { current: currentIndex, total: TOTAL_STEPS })}
        />
      </div>

      {step !== 'feedback' && (
        <section
          className="etch-mission-step"
          data-step={step}
          data-state={submitting ? 'busy' : 'active'}
          data-ai-generated="true"
          aria-labelledby="etch-mission-step-heading"
        >
          <h2
            className="etch-mission-step__heading"
            id="etch-mission-step-heading"
            ref={stepHeadingRef}
            tabIndex={-1}
          >
            {t(`missions.step.${step}`)}
          </h2>
          <p className="etch-mission-step__help">{t(`missions.step.${step}Help`)}</p>

          {step === 'recall' && (
            <TextArea
              label={steps?.recall?.prompt ?? t('missions.step.recall')}
              hint={steps?.recall?.hint || undefined}
              error={validation || undefined}
              rows={5}
              value={answers.recall}
              placeholder={t('missions.recallPlaceholder')}
              onChange={(event) => setAnswers((prev) => ({ ...prev, recall: event.target.value }))}
            />
          )}

          {step === 'apply' && (
            <>
              {steps?.apply?.variant?.scenario && (
                <p className="etch-mission-step__case">{steps.apply.variant.scenario}</p>
              )}
              <TextArea
                label={steps?.apply?.variant?.prompt ?? steps?.apply?.prompt ?? t('missions.step.apply')}
                error={validation || undefined}
                rows={5}
                value={answers.apply}
                placeholder={t('missions.applyPlaceholder')}
                onChange={(event) => setAnswers((prev) => ({ ...prev, apply: event.target.value }))}
              />
            </>
          )}

          {step === 'cite' && (
            <RadioGroup
              legend={steps?.cite?.prompt ?? t('missions.step.cite')}
              options={citeOptions}
              value={answers.cite ?? ''}
              error={validation || undefined}
              onChange={(value) => setAnswers((prev) => ({ ...prev, cite: value }))}
            />
          )}

          {step === 'confidence' && (
            <ConfidenceInput
              prompt={steps?.confidence?.prompt || undefined}
              scaleMax={steps?.confidence?.scaleMax ?? 5}
              value={answers.confidence}
              error={validation || undefined}
              onChange={(value) => setAnswers((prev) => ({ ...prev, confidence: value }))}
            />
          )}

          {stepError && (
            <Alert tone={stepError.code === 'session_expired' ? 'warning' : 'danger'} title={t(missionErrorKeys(stepError))}>
              {t('missions.stepFailedBody')}
            </Alert>
          )}

          <div className="etch-mission-step__actions">
            {canGoBack && (
              <Button variant="ghost" onClick={() => goToStep(previousStep)} disabled={submitting}>
                {t('missions.back')}
              </Button>
            )}
            <Button onClick={handleSubmit} loading={submitting}>
              {step === 'confidence' ? t('missions.reveal') : t('missions.next')}
            </Button>
          </div>
        </section>
      )}

      {step === 'feedback' && resolution && (
        <FeedbackPanel
          mission={mission}
          resolution={resolution}
          attemptIndex={attemptIndex}
          onRetry={handleRetry}
          onExit={onExit}
          onOpenSource={onOpenSource}
          busy={submitting}
        />
      )}
    </div>
  );
}

function nextStepAfter(step) {
  const index = MISSION_STEPS.indexOf(step);
  return MISSION_STEPS[Math.min(MISSION_STEPS.length - 1, index + 1)];
}

/**
 * Step 5. Explanation, what was right and wrong, the calibration read-out and
 * the next sensible action — never a score to lose (spec §2.3).
 */
function FeedbackPanel({ mission, resolution, attemptIndex, onRetry, onExit, onOpenSource, busy }) {
  const { t } = useTranslation();
  const headingRef = useRef(null);

  useEffect(() => {
    headingRef.current?.focus?.();
  }, []);

  const correctness = Number(resolution.correctness ?? 0);
  const passed = correctness >= 0.7;
  const verdict = calibrationVerdict(
    resolution.confidence,
    correctness,
    mission?.steps?.confidence?.scaleMax ?? 5
  );
  const mastery = resolution.mastery ?? { level: 'new', reasons: [] };
  const citeOption = (mission?.steps?.cite?.options ?? []).find(
    (option) => option.id === resolution.feedback?.citeCorrectOption
  );
  const sourceLabel =
    citeOption?.ref ||
    (typeof mission?.sourceRef === 'string'
      ? mission.sourceRef
      : mission?.sourceRef?.title || mission?.sourceRef?.ref || null);
  const nextRevisit = resolution.mastery?.nextRevisitAt
    ? new Date(resolution.mastery.nextRevisitAt)
    : null;

  return (
    <section
      className="etch-mission-feedback"
      data-outcome={passed ? 'pass' : 'open'}
      data-ai-generated="true"
      aria-labelledby="etch-mission-feedback-heading"
    >
      <div className="etch-mission-feedback__verdict">
        <h2 id="etch-mission-feedback-heading" ref={headingRef} tabIndex={-1}>
          {t(correctnessLabelKey(correctness))}
        </h2>
        <div className="etch-row">
          <Badge tone={resolution.citedCorrectly ? 'success' : undefined}>
            {resolution.citedCorrectly ? t('missions.citeCorrect') : t('missions.citeWrong')}
          </Badge>
          {resolution.transfer && <Badge tone="brand">{t('missions.transferShown')}</Badge>}
          <Badge>{t('missions.attemptNumber', { count: attemptIndex })}</Badge>
        </div>
      </div>

      {!passed && (
        <Alert tone="info" title={t('missions.learningPathTitle')}>
          {t('missions.learningPathBody')}
        </Alert>
      )}

      <div className="etch-mission-feedback__block">
        <h3>{t('missions.explanation')}</h3>
        <p className="etch-prose">{resolution.feedback?.explanation}</p>
      </div>

      {resolution.feedback?.recallExpected && (
        <div className="etch-mission-feedback__block">
          <h3>{t('missions.expectedRecall')}</h3>
          <p className="etch-prose">{resolution.feedback.recallExpected}</p>
        </div>
      )}

      {resolution.feedback?.applyExpected && (
        <div className="etch-mission-feedback__block">
          <h3>{t('missions.expectedApply')}</h3>
          <p className="etch-prose">{resolution.feedback.applyExpected}</p>
        </div>
      )}

      {citeOption && (
        <div className="etch-mission-feedback__block">
          <h3>{t('missions.source')}</h3>
          <p className="etch-prose">{citeOption.label}</p>
          {citeOption.ref && <p className="etch-mono etch-meta">{citeOption.ref}</p>}
        </div>
      )}

      <div className="etch-mission-feedback__block etch-calibration" data-verdict={verdict ?? 'unknown'}>
        <h3>{t('mastery.calibration')}</h3>
        <p>
          {verdict === 'overconfident' && t('mastery.calibrationOverconfidentText')}
          {verdict === 'underconfident' && t('mastery.calibrationUnderconfidentText')}
          {verdict === 'good' && t('mastery.calibrationGoodText')}
          {!verdict && t('mastery.calibrationUnknownText')}
        </p>
      </div>

      <div className="etch-mission-feedback__block">
        <h3>{t('mastery.timeline')}</h3>
        <MasteryMark level={mastery.level} reasons={mastery.reasons} showWhy />
        {mastery.levelChanged && (
          <p className="etch-meta">
            {t('mastery.levelChanged', {
              from: t(`mastery.level.${mastery.previousLevel ?? 'new'}`),
              to: t(`mastery.level.${mastery.level}`),
            })}
          </p>
        )}
      </div>

      <div className="etch-mission-feedback__next">
        <h3>{t('missions.nextAction')}</h3>
        <p>{t([`missions.action.${resolution.nextAction}`, 'missions.action.revisit_later'])}</p>
        {nextRevisit && (
          <p className="etch-meta">
            {t('missions.scheduledFor', { date: nextRevisit.toLocaleDateString() })}
          </p>
        )}
        <p className="etch-meta">{t('missions.noPenalty')}</p>
        <div className="etch-mission-feedback__actions">
          <Button onClick={onRetry} loading={busy}>
            {t('missions.tryAgain')}
          </Button>
          {sourceLabel && onOpenSource && (
            <Button variant="secondary" onClick={() => onOpenSource(sourceLabel)}>
              {t('missions.openSource')}
            </Button>
          )}
          <Button variant="ghost" onClick={onExit}>
            {t('missions.backToCenter')}
          </Button>
        </div>
      </div>
    </section>
  );
}

export default MissionRunner;
